"use client";
import React, { useCallback, useMemo, useState } from "react";
import ShopLayout from "./ShopLayout";
import AssortmentLayout from "./AssortmentLayout";
import AcademyLayout from "./AcademyLayout";
import ServicesLayout from "./ServicesLayout";
import Button from "../../theme/components/Button";
import { Link, useRouter } from "../../../navigation";
import Image from "next/image";
import MainNavigation from "./MainNavigation";
import NavigationWrapper from "./NavigationWrapper";
import { useTranslations } from "next-intl";
import useSearchSuggestions from "../../common/utils/useSearchSuggestions";
import useSuggester from "../../common/utils/useSuggester";
import useDebouncedSearchTerm from "../../common/utils/useDebouncedSearchTerm";
import { useParams } from "next/navigation";
import SearchResultDropdown from "./SearchResultDropdown";
import SearchHandlerInput from "./SearchHandlerInput";
import dynamic from "next/dynamic";
import CartSwitch from "../../portal/components/cart/CartSwitch";
import AvatarButton from "./AvatarButton";

const SearchResultWrapper = dynamic(() => import("./SearchResultWrapper"), {
  ssr: false,
});

const LayoutMap = {
  shop: ShopLayout,
  sortiment: AssortmentLayout, // "sortiment" is the German word for "assortment"
  services: ServicesLayout,
  academy: AcademyLayout,
};

const NavigationRenderer = ({ navigationData, children, additionalData }) => {
  const [showSearch, setShowSearch] = useState(false);
  const { storeSearchString } = useSuggester();

  const { queryString } = useParams();

  const router = useRouter();
  const [searchTerm, setSearchTerm, debouncedSearchTerm, isDebounced] =
    useDebouncedSearchTerm((queryString as string) || "", 500);

  const onSearch = (searchVal) => {
    if (!searchVal) return;

    storeSearchString(searchVal);
    const searchQuery =
      typeof searchVal === "object" ? searchVal.text : searchVal;
    router.push({
      pathname: `/search/${encodeURIComponent(searchQuery.replaceAll("/", "~"))}`,
    });

    setShowSearch(false);
  };

  const t = useTranslations();
  const {
    suggestions,
    products,
    contents,
    loading,
    totalMatchedProducts,
    assortments,
  } = useSearchSuggestions({
    queryString: debouncedSearchTerm,
    contentLimit: 4,
    suggestLimit: 10,
    assortmentLimit: 4,
  });

  const viewAllProductMatch = () => {
    onSearch(searchTerm);
  };
  const toggleSearch = useCallback(() => {
    setShowSearch(!showSearch);
  }, [showSearch]);

  const SearchInputComponent = useMemo(
    () => (
      <SearchHandlerInput
        onSearch={onSearch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onClick={() => {
          if (!showSearch) setShowSearch(true);
        }}
        onChange={() => {
          if (!showSearch) setShowSearch(true);
        }}
      />
    ),
    [searchTerm, showSearch],
  );
  return (
    <NavigationWrapper navigationData={navigationData}>
      {({
        selectedLayout,
        handleTopLevelClick,
        isMobileMenuOpen,
        currentNavigation,
        toggleMobileMenu,
        isMobile,
        menuRef,
        backToMainMenu,
      }) => {
        const Component = LayoutMap?.[selectedLayout];
        return (
          <div className="container">
            <SearchResultWrapper
              showSearch={showSearch}
              searchedTerm={searchTerm}
              productSuggestions={products}
              contentSuggestions={contents || []}
              typeAheadSuggestions={suggestions}
              categorySuggestions={assortments}
              onClose={() => setShowSearch(false)}
              isLoading={loading || isDebounced}
            >
              {(props) => (
                <SearchResultDropdown
                  onTypeAheadClick={onSearch}
                  foundProducts={totalMatchedProducts}
                  onViewAllProductMatch={viewAllProductMatch}
                  showMigelConformity={true}
                  {...props}
                />
              )}
            </SearchResultWrapper>
            <div className="row position-relative">
              <div
                className={`${showSearch ? "visible-on-mobile position-fixed z-high color-bg-white w-100 py-3 px-2 shadow" : "d-none"}`}
              >
                <div className="d-flex gap-2">
                  <div className="flex-grow-0 pt-2">
                    <Button
                      key={showSearch.toString()}
                      appearance="link"
                      icon="arrow-left"
                      onClick={toggleSearch}
                    />
                  </div>
                  <div style={{ width: "100%" }}>{SearchInputComponent}</div>
                </div>
              </div>

              <div className="py-3 py-lg-0 d-flex gap-3 align-items-center justify-content-between">
                <div
                  style={{ width: "126px" }}
                  className="d-flex align-items-center hide-on-not-mobile"
                >
                  <Button
                    key={showSearch.toString()}
                    appearance="link"
                    icon={showSearch ? "close" : "search"}
                    onClick={toggleSearch}
                    style={{
                      minWidth: "42px",
                      minHeight: "42px",
                    }}
                  />
                  <AvatarButton />
                  <CartSwitch />
                </div>
                <Link href="/">
                  <Image
                    src="/portal/img/publicare-logomark.png"
                    alt="Logo"
                    width={48}
                    height={48}
                    className="hide-on-not-mobile"
                  />
                </Link>

                <div style={{ width: "126px" }} className="text-end">
                  <Button
                    appearance="link"
                    iconPosition="right"
                    icon={isMobileMenuOpen ? "close" : "navigation-menu"}
                    className="hide-on-not-mobile"
                    aria-label="Toggle navigation"
                    onClick={toggleMobileMenu}
                  >
                    {isMobileMenuOpen ? t("close") : t("menu")}
                  </Button>
                </div>
              </div>

              <nav
                className={`mkt-nav ${isMobile && !isMobileMenuOpen ? "d-none" : ""}`}
                ref={menuRef}
              >
                <div className="d-flex flex-column flex-lg-column-reverse gap-5 gap-lg-3">
                  {Component && (
                    <div className="mkt-nav-content">
                      <Button
                        className="fs-8 color-primary-dimmed position-absolute hide-on-mobile"
                        appearance="link"
                        iconPosition="right"
                        icon="close"
                        onClick={toggleMobileMenu}
                        style={{ top: "2rem", right: "1rem" }}
                      >
                        <span className="hide-on-mobile">{t("close")}</span>
                      </Button>
                      <Component
                        data={currentNavigation}
                        config={additionalData?.[selectedLayout]}
                        backToMainMenu={backToMainMenu}
                      />
                    </div>
                  )}

                  <MainNavigation
                    navigationData={navigationData}
                    onTopLevelClick={handleTopLevelClick}
                    selectedLayout={selectedLayout}
                    SearchInputComponent={SearchInputComponent}
                  />
                  {(!isMobile || (!selectedLayout && isMobile)) && children}
                </div>
              </nav>
            </div>
          </div>
        );
      }}
    </NavigationWrapper>
  );
};

export default NavigationRenderer;
